import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete, canUserEdit } from '@/utils/permissions';
import { 
  Plus, Save, Search, Filter, Download, Edit2, Trash2, Eye,
  FileText, Package, Truck, Calendar, CheckCircle, AlertCircle, AlertTriangle,
  X, ChevronDown, RefreshCw, Loader2, Building2, Upload, Image, File
} from 'lucide-react';

// ISRI Scrap Types for Import GRN
const ISRI_SCRAP_TYPES = [
  'TALDON', 'TALC', 'TELIC', 'TALLY', 'TERSE', 'TAINT TABOR',
  'TENSE', 'TWITCH', 'ZORBA', 'TABLET', 'TRUMP', 'TEENS',
  'TASTE', 'TEPID', 'TREAD', 'TABOO', 'TAKES', 'TALON',
  'TROMA', 'TOUGH', 'TUTU', 'TWANG', 'TWIST', 'TOTO',
  'MIXED ALUMINIUM SCRAP', 'ALUMINIUM WHEELS', 'ALUMINIUM EXTRUSION',
  'ALUMINIUM CABLE', 'ALUMINIUM UTENSILS', 'ALUMINIUM TURNING',
  'UBC (USED BEVERAGE CANS)', 'ALUMINIUM DROSS', 'ALUMINIUM INGOTS'
];

// Searchable Dropdown for Suppliers
function SupplierSearchDropdown({ 
  value, 
  selectedSupplier,
  onSelect, 
  suppliers,
  loading,
  placeholder = "Search and select supplier..." 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  // Filter suppliers based on search
  const filteredSuppliers = suppliers.filter(s => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      s.name?.toLowerCase().includes(term) ||
      s.code?.toLowerCase().includes(term) ||
      s.gst?.toLowerCase().includes(term) ||
      s.city?.toLowerCase().includes(term) ||
      s.country?.toLowerCase().includes(term)
    );
  });
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left flex items-center justify-between hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-100 transition"
        data-testid="party-name-dropdown"
      >
        <span className={selectedSupplier ? 'text-gray-900 font-medium' : 'text-gray-400'}>
          {selectedSupplier?.name || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, code, GST, city..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
              {loading && <Loader2 size={16} className="text-green-500 animate-spin" />}
            </div>
          </div>
          
          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-6 text-center text-gray-500 text-sm">
                <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                Loading suppliers...
              </div>
            ) : filteredSuppliers.length === 0 ? (
              <div className="px-3 py-6 text-center text-gray-500 text-sm">
                <Building2 size={24} className="mx-auto mb-2 text-gray-300" />
                {searchTerm ? 'No suppliers found' : 'No suppliers available'}
              </div>
            ) : (
              filteredSuppliers.map((supplier, idx) => (
                <button
                  key={supplier.id || idx}
                  type="button"
                  onClick={() => {
                    onSelect(supplier);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-3 text-left hover:bg-green-50 transition border-b border-gray-50 ${
                    selectedSupplier?.id === supplier.id ? 'bg-green-100' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{supplier.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {supplier.code && <span className="mr-2">{supplier.code}</span>}
                        {supplier.city && <span className="mr-2">📍 {supplier.city}</span>}
                        {supplier.country && supplier.country !== 'India' && <span>{supplier.country}</span>}
                      </p>
                      {supplier.gst && (
                        <p className="text-xs text-blue-600 mt-0.5">GST: {supplier.gst}</p>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      supplier.status === 'Active' ? 'bg-green-100 text-green-700' :
                      supplier.status === 'Blacklisted' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {supplier.status || 'Active'}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GRN() {
  const { API, user } = useContext(AppContext);
  const [grnList, setGrnList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [saveStatus, setSaveStatus] = useState(null);
  
  // Check if user can delete (Admin or 'full' access on this page)
  const canDelete = canUserDelete(user, '/imports/grn');
  // Check if user can edit (Admin or 'edit'/'full' access on this page)
  const canEdit = canUserEdit(user, '/imports/grn');
  
  // Edit mode state for GRN details modal
  const [isEditingGRN, setIsEditingGRN] = useState(false);
  const [editInvoiceWeight, setEditInvoiceWeight] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingGRNEdit, setSavingGRNEdit] = useState(false);
  
  
  // Supplier data from master data
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  // PO data for supplier
  const [incompletePOs, setIncompletePOs] = useState([]);
  const [loadingPOs, setLoadingPOs] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedPOItem, setSelectedPOItem] = useState(null);
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Form state for new GRN
  const [formData, setFormData] = useState({
    grnNumber: '',
    invoiceNo: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    receivedDate: new Date().toISOString().split('T')[0],
    partyName: '',
    partyAddress: '',
    partyGST: '',
    poNumber: '', // Added PO reference
    currency: 'USD', // Currency from PO (default USD for imports)
    vehicleNo: '',
    driverName: '',
    containerNo: '',
    scrapType: '',
    invoiceWeight: '', // Invoice weight for comparison
    cfsWeight: '', // CFS (Container Freight Station) weight
    companyWeight: '', // Company's weighment
    grossWeight: '',
    tareWeight: '',
    packingWeight: '',
    netWeight: '',
    rate: '',
    amount: '',
    result: 'Accepted',
    remarks: '',
    status: 'Pending'
  });

  // ---- PO line guardrails (fix/grn-po-rate-guard) ----
  // Mirrors backend QTY_OVER_DELIVERY_TOLERANCE_PCT = 2.0
  const QTY_TOLERANCE_MULT = 1.02;
  const poDistinctRates = selectedPO ? new Set((selectedPO.items || []).map(i => i.rate)).size : 0;
  const poHasMultipleRates = poDistinctRates > 1;
  const lineRemainingQty = selectedPOItem ? (parseFloat(selectedPOItem.remainingQty) || 0) : null;
  const netQty = parseFloat(formData.netWeight) || 0;
  const qtyOverRemaining = lineRemainingQty !== null && netQty > lineRemainingQty + 1e-9;
  const qtyBeyondTolerance = lineRemainingQty !== null && netQty > (lineRemainingQty * QTY_TOLERANCE_MULT) + 1e-9;

  // Load GRN list and suppliers
  useEffect(() => {
    loadGRNList();
    loadSuppliers();
  }, []);
  
  // Load suppliers from master data
  const loadSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const response = await axios.get(`${API}/master-data/raw-material/suppliers`, {
        
      });
      // Filter only active suppliers
      const activeSuppliers = (response.data.data || []).filter(s => s.status !== 'Blacklisted');
      setSuppliers(activeSuppliers);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
    setLoadingSuppliers(false);
  };
  
  // Load incomplete POs for selected supplier
  const loadIncompletePOs = async (supplierCode, supplierName) => {
    if (!supplierCode && !supplierName) {
      setIncompletePOs([]);
      return;
    }
    
    setLoadingPOs(true);
    try {
      const params = new URLSearchParams();
      if (supplierName) params.append('supplier_name', supplierName);
      const response = await axios.get(
        `${API}/raw-material/purchase-orders/incomplete-by-supplier/${encodeURIComponent(supplierCode || supplierName)}?${params.toString()}`,
        
      );
      setIncompletePOs(response.data.data || []);
    } catch (error) {
      console.error('Error loading incomplete POs:', error);
      setIncompletePOs([]);
    }
    setLoadingPOs(false);
  };
  
  // Handle supplier selection - auto-fill address and GST, load POs
  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSelectedPO(null);
    setSelectedPOItem(null);
    setFormData(prev => ({
      ...prev,
      partyName: supplier.name || '',
      partyAddress: [supplier.address, supplier.city, supplier.state, supplier.country].filter(Boolean).join(', ') || '',
      partyGST: supplier.gst || '',
      poNumber: '',
      scrapType: '',
      rate: ''
    }));
    
    // Load incomplete POs for this supplier
    loadIncompletePOs(supplier.code || supplier.name, supplier.name);
  };
  
  // Handle PO selection - auto-fill scrap type and rate
  const handlePOSelect = (po) => {
    setSelectedPO(po);
    // Auto-select if PO has exactly 1 item
    if (po.items?.length === 1) {
      const item = po.items[0];
      setSelectedPOItem(item);
      const updatedData = {
        ...formData,
        poNumber: po.poNumber,
        currency: po.currency || 'USD',
        scrapType: item.scrapType,
        rate: item.rate || ''
      };
      const calculated = calculateValues(updatedData);
      updatedData.netWeight = calculated.netWeight || updatedData.netWeight;
      updatedData.amount = calculated.amount;
      setFormData(updatedData);
    } else {
      setSelectedPOItem(null);
      setFormData(prev => ({
        ...prev,
        poNumber: po.poNumber,
        currency: po.currency || 'USD',
        scrapType: '',
        rate: ''
      }));
    }
  };
  
  // Handle PO Item selection - auto-fill scrap type and rate
  const handlePOItemSelect = (item) => {
    setSelectedPOItem(item);
    const updatedData = {
      ...formData,
      scrapType: item.scrapType,
      rate: item.rate || ''
    };
    // Recalculate amount with the new rate
    const calculated = calculateValues(updatedData);
    updatedData.netWeight = calculated.netWeight || updatedData.netWeight;
    updatedData.amount = calculated.amount;
    setFormData(updatedData);
  };

  const loadGRNList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/raw-material/grn`, {
        
      });
      setGrnList(response.data);
    } catch (error) {
      console.error('Error loading GRN list:', error);
      // Sample data for demonstration
      setGrnList([
        {
          id: 'GRN-2026-001',
          grnNumber: 'GRN-2026-001',
          invoiceNo: 'INV-AM-2026-0145',
          invoiceDate: '2026-02-28',
          receivedDate: '2026-03-01',
          partyName: 'Ashvin Metals Pvt Ltd',
          partyAddress: 'Industrial Area, Mumbai',
          partyGST: '27AABCA1234F1ZP',
          vehicleNo: 'MH-04-AB-1234',
          driverName: 'Ramesh Kumar',
          scrapType: 'Ashvin Metals Turning',
          grossWeight: 5500,
          tareWeight: 450,
          packingWeight: 50,
          netWeight: 5000,
          rate: 185,
          amount: 925000,
          result: 'Accepted',
          remarks: 'Quality verified',
          status: 'Approved',
          availableQty: 3500,
          usedQty: 1500,
          createdAt: '2026-03-01T10:30:00Z'
        },
        {
          id: 'GRN-2026-002',
          grnNumber: 'GRN-2026-002',
          invoiceNo: 'INV-DH-2026-0089',
          invoiceDate: '2026-03-01',
          receivedDate: '2026-03-01',
          partyName: 'Dhatu Industries',
          partyAddress: 'MIDC Pune',
          partyGST: '27AABCD5678G1ZQ',
          vehicleNo: 'MH-12-CD-5678',
          driverName: 'Suresh Patil',
          scrapType: 'Dhatu Turning',
          grossWeight: 4000,
          tareWeight: 480,
          packingWeight: 20,
          netWeight: 3500,
          rate: 178,
          amount: 623000,
          result: 'Accepted',
          remarks: '',
          status: 'Approved',
          availableQty: 3500,
          usedQty: 0,
          createdAt: '2026-03-01T14:15:00Z'
        },
        {
          id: 'GRN-2026-003',
          grnNumber: 'GRN-2026-003',
          invoiceNo: 'INV-LT-2026-0234',
          invoiceDate: '2026-03-02',
          receivedDate: '2026-03-02',
          partyName: 'Lian Tian Trading',
          partyAddress: 'Nhava Sheva, Mumbai',
          partyGST: '27AABCL9012H1ZR',
          vehicleNo: 'MH-01-EF-9012',
          driverName: 'Abdul Khan',
          scrapType: 'Lian Tian Turning',
          grossWeight: 4500,
          tareWeight: 470,
          packingWeight: 30,
          netWeight: 4000,
          rate: 192,
          amount: 768000,
          remarks: 'Import consignment',
          status: 'Pending',
          availableQty: 4000,
          usedQty: 0,
          createdAt: '2026-03-02T09:00:00Z'
        }
      ]);
    }
    setLoading(false);
  };

  // Generate GRN number
  const generateGRNNumber = async () => {
    try {
      const response = await axios.get(`${API}/raw-material/grn/next-number`);
      return response.data.nextGRNNumber;
    } catch (error) {
      // Fallback: find max from loaded list
      const year = new Date().getFullYear();
      const prefix = `GRN-${year}-`;
      let maxNum = 0;
      grnList.forEach(g => {
        if (g.grnNumber && g.grnNumber.startsWith(prefix)) {
          const num = parseInt(g.grnNumber.replace(prefix, ''), 10);
          if (num > maxNum) maxNum = num;
        }
      });
      return `${prefix}${String(maxNum + 1).padStart(3, '0')}`;
    }
  };

  // Calculate net weight and amount
  const calculateValues = (data) => {
    const gross = parseFloat(data.grossWeight) || 0;
    const tare = parseFloat(data.tareWeight) || 0;
    const packing = parseFloat(data.packingWeight) || 0;
    const rate = parseFloat(data.rate) || 0;
    const net = Math.round((gross - tare - packing) * 1000000) / 1000000;
    const amount = Math.round(net * rate * 100) / 100;
    return { netWeight: net, amount: amount };
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    
    // Auto-calculate net weight and amount
    if (['grossWeight', 'tareWeight', 'packingWeight', 'rate'].includes(field)) {
      const calculated = calculateValues(updatedData);
      updatedData.netWeight = calculated.netWeight;
      updatedData.amount = calculated.amount;
    }
    
    setFormData(updatedData);
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newFiles = [];
    
    for (const file of files) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
        continue;
      }
      
      // Convert to base64 for storage
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => {
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
      
      newFiles.push({
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        name: file.name,
        type: file.type,
        size: file.size,
        data: fileData,
        uploadedAt: new Date().toISOString()
      });
    }
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
    setUploading(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Remove uploaded file
  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Save new GRN
  const handleSaveGRN = async () => {
    // Validate required fields
    if (!formData.invoiceWeight || parseFloat(formData.invoiceWeight) <= 0) {
      alert('Invoice Weight is mandatory. Please enter the weight as per invoice.');
      return;
    }
    
    if (!formData.grossWeight || !formData.netWeight) {
      alert('Please fill all required weight fields (Gross Weight, Net Weight).');
      return;
    }
    
    setSaveStatus('saving');
    
    const grnNumber = formData.grnNumber || await generateGRNNumber();
    const grnData = {
      grnNumber: grnNumber,
      invoiceNo: formData.invoiceNo,
      invoiceDate: formData.invoiceDate,
      receivedDate: formData.receivedDate,
      partyName: formData.partyName,
      partyAddress: formData.partyAddress,
      partyGST: formData.partyGST,
      poNumber: formData.poNumber || '', // PO reference
      poItemIndex: selectedPOItem?.poItemIndex ?? null, // authoritative PO line (multi-rate POs)
      currency: formData.currency || 'USD', // Currency from PO
      vehicleNo: formData.vehicleNo,
      driverName: formData.driverName,
      containerNo: formData.containerNo || '',
      scrapType: formData.scrapType,
      invoiceWeight: parseFloat(formData.invoiceWeight) || 0, // Invoice weight for comparison
      cfsWeight: parseFloat(formData.cfsWeight) || 0, // CFS weight
      companyWeight: parseFloat(formData.companyWeight) || 0, // Company weight
      grossWeight: parseFloat(formData.grossWeight) || 0,
      tareWeight: parseFloat(formData.tareWeight) || 0,
      packingWeight: parseFloat(formData.packingWeight) || 0,
      receivedQty: parseFloat(formData.netWeight) || 0,  // In MT for imports
      netWeight: parseFloat(formData.netWeight) || 0,    // In MT for imports
      rate: parseFloat(formData.rate) || 0,
      amount: Math.round((parseFloat(formData.amount) || 0) * 100) / 100,
      result: formData.result,
      remarks: formData.remarks,
      unit: 'MT',  // Changed to MT for imports
      availableQty: parseFloat(formData.netWeight) || 0,
      usedQty: 0,
      // Include uploaded files
      attachments: uploadedFiles.map(f => ({
        id: f.id,
        name: f.name,
        type: f.type,
        size: f.size,
        data: f.data,
        uploadedAt: f.uploadedAt
      })),
      createdAt: new Date().toISOString()
    };

    try {
      await axios.post(`${API}/raw-material/grn`, grnData, {
        
      });
      
      // Auto-create Weighment Claim ONLY if:
      // 1. Result is marked as "Shortage" (data entry person indicated shortage)
      // 2. AND there is actual shortage (Invoice Weight > CFS Weight)
      const invoiceWt = parseFloat(formData.invoiceWeight) || 0;
      const cfsWt = parseFloat(formData.cfsWeight) || 0;
      const shortage = invoiceWt - cfsWt; // Claim based on Invoice Weight minus CFS Weight
      
      // Only create claim if shortage is indicated AND CFS weight shows shortage
      if (formData.result === 'Shortage' && invoiceWt > 0 && cfsWt > 0 && shortage > 0) {
        try {
          const claimData = {
            claimNumber: `WC/${grnData.grnNumber.replace('GRN/', '')}`,
            grnNumber: grnData.grnNumber,
            invoiceNo: grnData.invoiceNo,
            partyName: grnData.partyName,
            poNumber: grnData.poNumber || '',
            claimType: 'Weighment Claim',
            claimDate: new Date().toISOString().split('T')[0],
            invoiceWeight: invoiceWt,
            cfsWeight: cfsWt,
            companyWeight: parseFloat(formData.companyWeight) || 0,
            actualWeight: cfsWt, // Use CFS weight as actual for claim calculation
            shortageQty: shortage, // Invoice Weight - CFS Weight
            claimAmount: shortage * (parseFloat(formData.rate) || 0),
            currency: grnData.currency || 'USD',
            description: `Weighment claim for shortage of ${shortage.toFixed(3)} MT. Invoice Weight: ${invoiceWt.toFixed(3)} MT, CFS Weight: ${cfsWt.toFixed(3)} MT`,
            status: 'Pending',
            createdBy: 'System (Auto-generated)',
            createdAt: new Date().toISOString(),
            // Additional details for PDF
            scrapType: grnData.scrapType,
            rate: parseFloat(formData.rate) || 0,
            vehicleNo: grnData.vehicleNo
          };
          
          await axios.post(`${API}/imports/claims`, claimData);
          console.log('Weighment claim created automatically');
        } catch (claimError) {
          console.error('Error creating automatic claim:', claimError);
        }
      }
      
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus(null);
        setShowAddModal(false);
        loadGRNList();
        // Reset form and PO state
        setFormData({
          grnNumber: '',
          invoiceNo: '',
          invoiceDate: new Date().toISOString().split('T')[0],
          receivedDate: new Date().toISOString().split('T')[0],
          partyName: '',
          partyAddress: '',
          partyGST: '',
          poNumber: '',
          currency: 'USD',
          vehicleNo: '',
          driverName: '',
          containerNo: '',
          scrapType: '',
          invoiceWeight: '',
          cfsWeight: '',
          companyWeight: '',
          grossWeight: '',
          tareWeight: '',
          packingWeight: '',
          netWeight: '',
          rate: '',
          amount: '',
          result: 'Accepted',
          remarks: '',
          status: 'Pending'
        });
        setSelectedSupplier(null);
        setSelectedPO(null);
        setSelectedPOItem(null);
        setIncompletePOs([]);
        setUploadedFiles([]); // Reset uploaded files
      }, 1500);
    } catch (error) {
      console.error('Error saving GRN:', error.response?.data || error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      setSaveStatus('error');
      alert(`Error saving GRN: ${errorMsg}`);
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  // Update GRN status
  const handleUpdateStatus = async (grnId, newStatus) => {
    try {
      await axios.put(`${API}/raw-material/grn/${grnId}`, {
        status: newStatus
      });
      loadGRNList();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Delete GRN
  const handleDeleteGRN = async (grnId) => {
    if (!window.confirm('Are you sure you want to delete this GRN?')) return;
    
    try {
      await axios.delete(`${API}/raw-material/grn/${grnId}`, {
        
      });
      loadGRNList();
    } catch (error) {
      console.error('Error deleting GRN:', error);
    }
  };

  // Save Invoice Weight Edit
  const handleSaveInvoiceWeight = async () => {
    if (!selectedGRN) return;
    
    setSavingEdit(true);
    try {
      await axios.put(`${API}/raw-material/grn/${selectedGRN.grnNumber}/invoice-weight`, {
        invoiceWeight: parseFloat(editInvoiceWeight) || 0
      });
      
      // Update local state
      setSelectedGRN(prev => ({
        ...prev,
        invoiceWeight: parseFloat(editInvoiceWeight) || 0
      }));
      
      setIsEditingGRN(false);
      loadGRNList(); // Refresh list
    } catch (error) {
      console.error('Error saving invoice weight:', error);
      alert('Failed to save invoice weight');
    } finally {
      setSavingEdit(false);
    }
  };

  // Open edit modal for a GRN
  const openEditModal = (grn) => {
    setEditData({
      grnNumber: grn.grnNumber || grn.id,
      result: grn.result || 'Accepted',
      invoiceWeight: grn.invoiceWeight || '',
      cfsWeight: grn.cfsWeight || '',
      companyWeight: grn.companyWeight || '',
      grossWeight: grn.grossWeight || '',
      tareWeight: grn.tareWeight || '',
      packingWeight: grn.packingWeight || '',
      netWeight: grn.netWeight || grn.receivedQty || '',
      rate: grn.rate || '',
      amount: grn.amount || '',
      remarks: grn.remarks || '',
      status: grn.status || 'Pending',
      receivedDate: grn.receivedDate || '',
      scrapType: grn.scrapType || '',
      containerNo: grn.containerNo || '',
    });
    setShowEditModal(true);
  };

  // Handle edit field change with auto-calculation
  const handleEditChange = (field, value) => {
    setEditData(prev => {
      const updated = { ...prev, [field]: value };
      if (['grossWeight', 'tareWeight', 'packingWeight', 'rate'].includes(field)) {
        const gross = parseFloat(updated.grossWeight) || 0;
        const tare = parseFloat(updated.tareWeight) || 0;
        const packing = parseFloat(updated.packingWeight) || 0;
        const rate = parseFloat(updated.rate) || 0;
        const net = gross - tare - packing;
        updated.netWeight = net;
        updated.amount = net * rate;
      }
      return updated;
    });
  };

  // Save GRN edit
  const handleSaveGRNEdit = async () => {
    setSavingGRNEdit(true);
    try {
      const payload = {
        result: editData.result,
        invoiceWeight: parseFloat(editData.invoiceWeight) || 0,
        cfsWeight: parseFloat(editData.cfsWeight) || 0,
        companyWeight: parseFloat(editData.companyWeight) || 0,
        grossWeight: parseFloat(editData.grossWeight) || 0,
        tareWeight: parseFloat(editData.tareWeight) || 0,
        packingWeight: parseFloat(editData.packingWeight) || 0,
        rate: parseFloat(editData.rate) || 0,
        remarks: editData.remarks,
        status: editData.status,
        receivedDate: editData.receivedDate || undefined,
        scrapType: editData.scrapType || undefined,
      };
      await axios.put(`${API}/raw-material/grn/${editData.grnNumber}`, payload);
      setShowEditModal(false);
      loadGRNList();
    } catch (error) {
      console.error('Error updating GRN:', error);
      alert('Failed to update GRN');
    } finally {
      setSavingGRNEdit(false);
    }
  };

  // Filter GRN list
  const filteredList = grnList.filter(grn => {
    const matchesSearch = 
      grn.grnNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grn.invoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grn.partyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grn.scrapType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'All' || grn.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Summary stats
  const totalGRN = grnList.length;
  const pendingGRN = grnList.filter(g => g.status === 'Pending').length;
  const approvedGRN = grnList.filter(g => g.status === 'Approved').length;
  
  // Calculate totals by currency
  const totalValueUSD = grnList
    .filter(g => g.currency === 'USD')
    .reduce((sum, g) => sum + (g.amount || 0), 0);
  const totalValueINR = grnList
    .filter(g => g.currency !== 'USD')
    .reduce((sum, g) => sum + (g.amount || 0), 0);
  
  const totalStock = grnList.reduce((sum, g) => sum + (g.availableQty || 0), 0);

  return (
    <div className="space-y-4" data-testid="grn-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
            <FileText className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Import GRN - Goods Receipt Note</h1>
            <p className="text-sm text-gray-500">Track incoming imported scrap with invoice linking for production</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search GRN, Invoice, Party..."
              className="bg-transparent border-none outline-none text-sm w-48"
              data-testid="search-input"
            />
          </div>

          {/* Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-blue-400"
            data-testid="status-filter"
          >
            <option value="All">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>

          {/* Refresh */}
          <button
            onClick={loadGRNList}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={16} className="text-gray-600" />
          </button>

          {/* Dedup GRNs — Preview First, Then Execute */}
          <button
            onClick={async () => {
              try {
                // Step 1: Preview what would happen
                const preview = await axios.post(`${API}/raw-material/grn/dedup/preview`);
                const p = preview.data;
                
                if (p.wouldRemove === 0 && p.wouldRename === 0) {
                  alert(`No duplicates found among ${p.totalGRNs} GRNs. Everything looks clean!`);
                  return;
                }
                
                let msg = `PREVIEW (no changes yet):\nTotal GRNs: ${p.totalGRNs}\n`;
                if (p.wouldRemove > 0) msg += `\nWould REMOVE ${p.wouldRemove} exact duplicate(s):\n` + p.removals.map(r => `  • ${r.grnNumber} - ${r.partyName} (${r.invoiceNo})`).join('\n');
                if (p.wouldRename > 0) msg += `\n\nWould RENAME ${p.wouldRename} GRN number conflict(s):\n` + p.renames.map(r => `  • ${r.oldNumber} → ${r.newNumber} (${r.partyName})`).join('\n');
                msg += '\n\nProceed with these changes? (Deleted records will be backed up for recovery)';
                
                if (!window.confirm(msg)) return;
                
                // Step 2: Execute
                const res = await axios.post(`${API}/raw-material/grn/dedup`);
                const d = res.data;
                alert(d.message + (d.note ? '\n' + d.note : ''));
                loadGRNList();
              } catch (err) {
                alert('Dedup failed: ' + (err.response?.data?.detail || err.message));
              }
            }}
            className="px-3 py-2 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 transition text-sm font-medium"
            title="Preview and fix duplicate GRNs (with backup)"
            data-testid="dedup-grn-btn"
          >
            Fix Duplicates
          </button>

          {/* Restore Deleted GRNs */}
          <button
            onClick={async () => {
              if (!window.confirm('This will restore ALL GRN records deleted by the last dedup run. Continue?')) return;
              try {
                const res = await axios.post(`${API}/raw-material/grn/dedup/restore`);
                alert(res.data.message);
                loadGRNList();
              } catch (err) {
                alert('Restore failed: ' + (err.response?.data?.detail || err.message));
              }
            }}
            className="px-3 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition text-sm font-medium"
            title="Restore GRNs deleted by last dedup"
            data-testid="restore-grn-btn"
          >
            Restore Deleted
          </button>

          {/* Recover GRNs from Claims/Consumption data */}
          <button
            onClick={async () => {
              try {
                const preview = await axios.get(`${API}/raw-material/grn/recover/preview`);
                const p = preview.data;
                if (p.recoverableGRNs === 0) {
                  alert(`No missing GRNs found. All ${p.existingGRNs} GRN references in claims/consumption already exist.`);
                  return;
                }
                let msg = `Found ${p.recoverableGRNs} GRN(s) in claims/consumption that are MISSING from GRN records:\n\n`;
                p.records.forEach(r => {
                  const val = r.amount ? ` | Value: ${r.currency === 'INR' ? '₹' : '$'}${r.amount.toLocaleString()}` : '';
                  msg += `• ${r.grnNumber} — ${r.partyName} | ${r.invoiceNo} | Qty: ${r.receivedQty} MT | Rate: ${r.rate}${val} (from: ${r.sources.join(', ')})\n`;
                });
                msg += `\nRecover all ${p.recoverableGRNs} GRNs? (Status will be set to "Recovered" for review)`;
                if (!window.confirm(msg)) return;

                const res = await axios.post(`${API}/raw-material/grn/recover`);
                alert(res.data.message + '\n' + (res.data.note || ''));
                loadGRNList();
              } catch (err) {
                alert('Recovery failed: ' + (err.response?.data?.detail || err.message));
              }
            }}
            className="px-3 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition text-sm font-medium"
            title="Recover missing GRNs from claims and consumption data"
            data-testid="recover-grn-btn"
          >
            Recover GRNs
          </button>

          {/* Fix Recovered GRN Values */}
          <button
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/raw-material/grn/fix-recovered-values`);
                const d = res.data;
                if (d.fixed === 0) {
                  alert('No recovered GRNs need fixing — all values are already populated.');
                  return;
                }
                let msg = `Fixed ${d.fixed} recovered GRN(s) with actual values:\n\n`;
                d.details.forEach(r => {
                  msg += `• ${r.grnNumber} — Net Wt: ${r.netWeight} MT, Amount: ${r.amount}, Fields: ${r.fieldsUpdated.join(', ')}\n`;
                });
                alert(msg);
                loadGRNList();
              } catch (err) {
                alert('Fix failed: ' + (err.response?.data?.detail || err.message));
              }
            }}
            className="px-3 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition text-sm font-medium"
            title="Fix zero weights/amounts on recovered GRNs"
            data-testid="fix-recovered-values-btn"
          >
            Fix Values
          </button>

          {/* Add New GRN */}
          <button
            onClick={async () => {
              setSelectedSupplier(null);
              setSelectedPO(null);
              setSelectedPOItem(null);
              setIncompletePOs([]);
              setUploadedFiles([]); // Reset uploaded files
              const nextGRN = await generateGRNNumber();
              setFormData({
                grnNumber: nextGRN,
                invoiceNo: '',
                invoiceDate: new Date().toISOString().split('T')[0],
                receivedDate: new Date().toISOString().split('T')[0],
                partyName: '',
                partyAddress: '',
                partyGST: '',
                poNumber: '',
                currency: 'USD', // Default currency for imports
                vehicleNo: '',
                driverName: '',
                containerNo: '',
                scrapType: '',
                invoiceWeight: '',
                cfsWeight: '',
                companyWeight: '',
                grossWeight: '',
                tareWeight: '',
                packingWeight: '',
                netWeight: '',
                rate: '',
                amount: '',
                result: 'Accepted',
                remarks: '',
                status: 'Pending'
              });
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition text-sm font-medium"
            data-testid="add-grn-btn"
          >
            <Plus size={16} />
            New GRN
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total GRN</p>
          <p className="text-2xl font-bold">{totalGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Pending</p>
          <p className="text-2xl font-bold">{pendingGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Approved</p>
          <p className="text-2xl font-bold">{approvedGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total Value (USD)</p>
          <p className="text-xl font-bold">${totalValueUSD.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total Value (INR)</p>
          <p className="text-xl font-bold">₹{(totalValueINR / 100000).toFixed(1)}L</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Available Stock</p>
          <p className="text-xl font-bold">{totalStock.toFixed(1)} MT</p>
        </div>
      </div>

      {/* GRN Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <Package size={16} className="text-green-500" />
            GRN List - Linked to Production Data Entry
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">GRN #</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Invoice No</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Party Name</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">PO Number</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Scrap Type</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Gross</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Tare</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Packing</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Net Wt</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Unit</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Result</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Amount</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Status</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={15} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-green-500 border-t-transparent"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : filteredList.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-4 py-8 text-center text-gray-500">
                    No GRN records found
                  </td>
                </tr>
              ) : (
                filteredList.map((grn) => (
                  <tr key={grn.id} className="hover:bg-green-50/50 transition" data-testid={`grn-row-${grn.grnNumber}`}>
                    <td className="px-3 py-2">
                      <span className="font-mono text-green-600 font-medium">{grn.grnNumber}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-medium text-blue-600">{grn.invoiceNo}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{grn.receivedDate}</td>
                    <td className="px-3 py-2 font-medium">{grn.partyName}</td>
                    <td className="px-3 py-2">
                      {grn.poNumber ? (
                        <span className="font-mono text-amber-700 text-xs bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200" data-testid={`po-link-${grn.grnNumber}`}>
                          {grn.poNumber}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-700">{grn.scrapType}</span>
                    </td>
                    <td className="px-3 py-2 text-right">{grn.unit === 'MT' ? grn.grossWeight?.toFixed(3) : grn.grossWeight?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{grn.unit === 'MT' ? grn.tareWeight?.toFixed(3) : grn.tareWeight?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{grn.unit === 'MT' ? (grn.packingWeight?.toFixed(3) || '0.000') : (grn.packingWeight?.toLocaleString() || 0)}</td>
                    <td className="px-3 py-2 text-right font-bold text-blue-600">{grn.unit === 'MT' ? (grn.netWeight?.toFixed(3) || grn.receivedQty?.toFixed(3)) : (grn.netWeight?.toLocaleString() || grn.receivedQty?.toLocaleString())}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${grn.unit === 'MT' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                        {grn.unit || 'kg'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        grn.result === 'Accepted' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {grn.result || 'Accepted'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {grn.currency === 'USD' ? '$' : grn.currency === 'EUR' ? '€' : '₹'}
                      {grn.amount?.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        grn.status === 'Approved' ? 'bg-green-100 text-green-700' :
                        grn.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {grn.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedGRN(grn);
                            setShowViewModal(true);
                          }}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded transition"
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => openEditModal(grn)}
                            className="p-1 text-amber-500 hover:bg-amber-100 rounded transition"
                            title="Edit GRN"
                            data-testid={`edit-grn-${grn.grnNumber}`}
                          >
                            <Edit2 size={14} />
                          </button>
                        )}
                        {grn.status !== 'Approved' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(grn.id, 'Approved')}
                              className="p-1 text-green-500 hover:bg-green-100 rounded transition"
                              title="Approve"
                            >
                              <CheckCircle size={14} />
                            </button>
                            {canDelete && (
                              <button
                                onClick={() => handleDeleteGRN(grn.id)}
                                className="p-1 text-red-500 hover:bg-red-100 rounded transition"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <h4 className="font-semibold mb-2">How GRN Links to Production:</h4>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li><strong>Create GRN</strong> when scrap arrives with invoice details</li>
          <li><strong>Approve GRN</strong> to make scrap available for production</li>
          <li>In <strong>Production Data Entry</strong>, select invoice from dropdown - auto-fills scrap type & party</li>
          <li><strong>Available</strong> shows remaining quantity, <strong>Used</strong> shows what's been consumed in production</li>
        </ul>
      </div>

      {/* Add GRN Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <FileText className="text-green-500" size={20} />
                <h2 className="font-bold text-lg">New Import GRN - Goods Receipt Note</h2>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* GRN & Invoice Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">GRN Number</label>
                  <input
                    type="text"
                    value={formData.grnNumber}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number *</label>
                  <input
                    type="text"
                    value={formData.invoiceNo}
                    onChange={(e) => handleInputChange('invoiceNo', e.target.value)}
                    placeholder="INV-XXX-2026-0001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                    data-testid="invoice-no-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={formData.invoiceDate}
                    onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Received Date</label>
                  <input
                    type="date"
                    value={formData.receivedDate}
                    onChange={(e) => handleInputChange('receivedDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                  />
                </div>
              </div>

              {/* Party Details */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Building2 size={16} className="text-green-500" />
                  Party Details (from Supplier Dashboard)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Party Name *</label>
                    <SupplierSearchDropdown
                      value={formData.partyName}
                      selectedSupplier={selectedSupplier}
                      onSelect={handleSupplierSelect}
                      suppliers={suppliers}
                      loading={loadingSuppliers}
                      placeholder="Search and select supplier..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Party GST</label>
                    <input
                      type="text"
                      value={formData.partyGST}
                      onChange={(e) => handleInputChange('partyGST', e.target.value)}
                      placeholder="Auto-filled from supplier"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:border-green-400 outline-none"
                      readOnly={!!selectedSupplier?.gst}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Party Address</label>
                    <input
                      type="text"
                      value={formData.partyAddress}
                      onChange={(e) => handleInputChange('partyAddress', e.target.value)}
                      placeholder="Auto-filled from supplier"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:border-green-400 outline-none"
                      readOnly={!!selectedSupplier?.address}
                    />
                  </div>
                </div>
              </div>

              {/* PO Selection - Required when supplier is selected */}
              {selectedSupplier && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText size={16} className="text-blue-500" />
                    Link to Purchase Order <span className="text-red-500">*</span>
                  </h3>
                  
                  {loadingPOs ? (
                    <div className="flex items-center gap-2 text-gray-500 text-sm py-3">
                      <Loader2 size={16} className="animate-spin" />
                      Loading incomplete POs...
                    </div>
                  ) : incompletePOs.length === 0 ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      <AlertCircle size={16} className="inline mr-2" />
                      No incomplete Purchase Orders found for this supplier. Please create a PO first before creating GRN.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* PO Dropdown */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Select Purchase Order <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.poNumber}
                          onChange={(e) => {
                            const po = incompletePOs.find(p => p.poNumber === e.target.value);
                            if (po) {
                              handlePOSelect(po);
                            } else {
                              setSelectedPO(null);
                              setSelectedPOItem(null);
                              setFormData(prev => ({ ...prev, poNumber: '', scrapType: '', rate: '' }));
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:border-blue-400 outline-none ${
                            !formData.poNumber ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          data-testid="po-select"
                          required
                        >
                          <option value="">-- Select PO (Required) --</option>
                          {incompletePOs.map(po => (
                            <option key={po.id} value={po.poNumber}>
                              {po.poNumber} | {po.poDate} | {po.totalRemainingQty?.toFixed(2)} MT remaining | {po.currency} {po.totalAmount?.toLocaleString()}
                            </option>
                          ))}
                        </select>
                        {!formData.poNumber && (
                          <p className="text-xs text-red-500 mt-1">Please select a Purchase Order</p>
                        )}
                      </div>
                      
                      {/* PO Item Selection - Show when PO is selected */}
                      {selectedPO && selectedPO.items?.length > 1 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Select Material Type from PO <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={selectedPOItem ? String(selectedPO.items.indexOf(selectedPOItem)) : ''}
                            onChange={(e) => {
                              const idx = e.target.value === '' ? -1 : parseInt(e.target.value, 10);
                              const item = idx >= 0 ? selectedPO.items[idx] : null;
                              if (item) {
                                handlePOItemSelect(item);
                              } else {
                                setSelectedPOItem(null);
                                setFormData(prev => ({ ...prev, scrapType: '', rate: '', amount: '' }));
                              }
                            }}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:border-blue-400 outline-none ${
                              !selectedPOItem ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            data-testid="po-material-select"
                            required
                          >
                            <option value="">-- Select Material Type --</option>
                            {selectedPO.items.map((item, idx) => (
                              <option key={idx} value={String(idx)}>
                                {item.scrapType} | Remaining: {item.remainingQty?.toFixed(2)} MT | {selectedPO.currency} {item.rate}/MT
                              </option>
                            ))}
                          </select>
                          {selectedPOItem && (
                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                              <CheckCircle size={12} />
                              {selectedPOItem.scrapType}: {selectedPOItem.remainingQty?.toFixed(2)} MT remaining @ {selectedPO.currency} {selectedPOItem.rate}/MT
                            </p>
                          )}
                        </div>
                      )}

                      {/* Multi-rate PO notice — each line must be received as its own GRN */}
                      {poHasMultipleRates && (
                        <div className="mt-2 bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="multi-rate-po-notice">
                          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                          <span>
                            <strong>This PO has lines with different rates.</strong> Receive each line as a
                            separate GRN — one GRN can carry only one line's rate and quantity.
                          </span>
                        </div>
                      )}

                      {/* Over-remaining quantity hint (2% tolerance mirrors backend) */}
                      {qtyBeyondTolerance && (
                        <div className="mt-2 bg-red-50 border border-red-300 rounded-lg p-3 text-xs text-red-700 flex items-start gap-2" data-testid="qty-over-tolerance-warning">
                          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                          <span>
                            Net weight <strong>{netQty.toFixed(3)} MT</strong> exceeds this line's remaining
                            quantity of <strong>{lineRemainingQty.toFixed(3)} MT</strong> by more than the
                            allowed 2% tolerance (max {(lineRemainingQty * QTY_TOLERANCE_MULT).toFixed(3)} MT).
                            The server will reject this GRN.
                          </span>
                        </div>
                      )}
                      {qtyOverRemaining && !qtyBeyondTolerance && (
                        <div className="mt-2 bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="qty-over-remaining-hint">
                          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                          <span>
                            Net weight exceeds the line's remaining quantity ({lineRemainingQty.toFixed(3)} MT)
                            but is within the 2% tolerance.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Vehicle & Container Details */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Vehicle & Container Details</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Container Number</label>
                    <input
                      type="text"
                      value={formData.containerNo}
                      onChange={(e) => handleInputChange('containerNo', e.target.value)}
                      placeholder="ABCU1234567"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                      data-testid="grn-container-no"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle Number</label>
                    <input
                      type="text"
                      value={formData.vehicleNo}
                      onChange={(e) => handleInputChange('vehicleNo', e.target.value)}
                      placeholder="MH-04-AB-1234"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Driver Name</label>
                    <input
                      type="text"
                      value={formData.driverName}
                      onChange={(e) => handleInputChange('driverName', e.target.value)}
                      placeholder="Driver name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Material Details */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Material Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Scrap Type *
                      {selectedPOItem && (
                        <span className="text-xs text-blue-500 ml-1">(from PO: {selectedPOItem.scrapType})</span>
                      )}
                    </label>
                    <select
                      value={formData.scrapType}
                      onChange={(e) => handleInputChange('scrapType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                      data-testid="scrap-type-select"
                      required
                    >
                      <option value="">-- Select Scrap Type --</option>
                      {ISRI_SCRAP_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Weight (MT) *</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.invoiceWeight}
                      onChange={(e) => handleInputChange('invoiceWeight', e.target.value)}
                      placeholder="As per invoice"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                      data-testid="invoice-weight-input"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Weight mentioned in invoice (mandatory)</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-amber-700 mb-1">CFS Weight (MT) *</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.cfsWeight}
                      onChange={(e) => handleInputChange('cfsWeight', e.target.value)}
                      placeholder="Weight at CFS"
                      required
                      className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:border-amber-500 outline-none bg-amber-50"
                      data-testid="cfs-weight-input"
                    />
                    <p className="text-[10px] text-amber-600 mt-1">Container Freight Station weight (for claim calculation)</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-700 mb-1">Company Weight (MT)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.companyWeight}
                      onChange={(e) => handleInputChange('companyWeight', e.target.value)}
                      placeholder="Your weighment"
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:border-blue-500 outline-none bg-blue-50"
                      data-testid="company-weight-input"
                    />
                    <p className="text-[10px] text-blue-600 mt-1">Company's own weighment</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Gross Weight (MT) *</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.grossWeight}
                      onChange={(e) => handleInputChange('grossWeight', e.target.value)}
                      placeholder="0.000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                      data-testid="gross-weight-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tare Weight (MT) *</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.tareWeight}
                      onChange={(e) => handleInputChange('tareWeight', e.target.value)}
                      placeholder="0.000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                      data-testid="tare-weight-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Packing Weight (MT)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.packingWeight}
                      onChange={(e) => handleInputChange('packingWeight', e.target.value)}
                      placeholder="0.000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none"
                      data-testid="packing-weight-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Net Weight (MT)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.netWeight}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-blue-50 font-bold text-blue-700"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Gross - Tare - Packing</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Result *</label>
                    <select
                      value={formData.result}
                      onChange={(e) => handleInputChange('result', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none ${
                        formData.result === 'Accepted' ? 'border-green-400 bg-green-50 text-green-700' : 'border-red-400 bg-red-50 text-red-700'
                      }`}
                      data-testid="result-select"
                    >
                      <option value="Accepted">Accepted</option>
                      <option value="Shortage">Shortage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Rate ({formData.currency || 'USD'}/MT)
                      {selectedPO && <span className="text-xs text-blue-500 ml-1">(from PO)</span>}
                    </label>
                    <input
                      type="number"
                      value={formData.rate}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Total Amount ({formData.currency || 'USD'})
                    </label>
                    <input
                      type="number"
                      value={formData.amount}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-green-50 font-bold text-green-700"
                    />
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  placeholder="Any additional notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-green-400 outline-none resize-none"
                />
              </div>
              
              {/* File Upload Section */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Upload size={16} className="text-blue-500" />
                  Attachments (Invoice, Weighbridge Slip, Photos)
                </h3>
                
                {/* Upload Button */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <div className="flex flex-col items-center gap-2">
                    {uploading ? (
                      <Loader2 size={24} className="text-blue-500 animate-spin" />
                    ) : (
                      <Upload size={24} className="text-gray-400" />
                    )}
                    <p className="text-sm text-gray-500">
                      {uploading ? 'Uploading...' : 'Click to upload files or drag and drop'}
                    </p>
                    <p className="text-xs text-gray-400">
                      Images, PDF, Word, Excel (Max 10MB each)
                    </p>
                  </div>
                </div>
                
                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium text-gray-600">{uploadedFiles.length} file(s) attached</p>
                    {uploadedFiles.map(file => (
                      <div key={file.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 border border-gray-200">
                        <div className="flex items-center gap-2">
                          {file.type.startsWith('image/') ? (
                            <div className="w-10 h-10 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                              <img src={file.data} alt={file.name} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <File size={20} className="text-blue-500" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate max-w-[200px]">{file.name}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(file.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded transition"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGRN}
                disabled={saveStatus === 'saving' || !formData.invoiceNo || !formData.partyName || !formData.scrapType || !formData.poNumber || qtyBeyondTolerance}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  saveStatus === 'saved' 
                    ? 'bg-green-500 text-white' 
                    : saveStatus === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid="save-grn-btn"
              >
                {saveStatus === 'saving' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Saving...
                  </>
                ) : saveStatus === 'saved' ? (
                  <>
                    <CheckCircle size={16} />
                    Saved!
                  </>
                ) : saveStatus === 'error' ? (
                  <>
                    <AlertCircle size={16} />
                    Error
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save GRN
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View GRN Modal */}
      {showViewModal && selectedGRN && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Eye className="text-blue-500" size={20} />
                <h2 className="font-bold text-lg">GRN Details - {selectedGRN.grnNumber}</h2>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Status Badge */}
              <div className="flex justify-between items-center">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedGRN.status === 'Approved' ? 'bg-green-100 text-green-700' :
                  selectedGRN.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {selectedGRN.status}
                </span>
                <span className="text-xs text-gray-500">
                  Created: {new Date(selectedGRN.createdAt).toLocaleString()}
                </span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Invoice Number</p>
                  <p className="font-medium text-blue-600">{selectedGRN.invoiceNo}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Received Date</p>
                  <p className="font-medium">{selectedGRN.receivedDate}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs">Party Name</p>
                  <p className="font-medium">{selectedGRN.partyName}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs">Address</p>
                  <p className="font-medium">{selectedGRN.partyAddress || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">GST Number</p>
                  <p className="font-medium">{selectedGRN.partyGST || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Vehicle Number</p>
                  <p className="font-medium">{selectedGRN.vehicleNo || '-'}</p>
                </div>
                <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 text-xs font-semibold uppercase tracking-wide">Linked Purchase Order</p>
                  {selectedGRN.poNumber ? (
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="font-mono text-amber-900 font-bold text-base" data-testid="view-grn-po-number">{selectedGRN.poNumber}</span>
                      {selectedGRN.currency && (
                        <span className="text-xs text-amber-700">Currency: <strong>{selectedGRN.currency}</strong></span>
                      )}
                      {selectedGRN.rate ? (
                        <span className="text-xs text-amber-700">
                          Rate Applied: <strong>{selectedGRN.currency === 'USD' ? '$' : selectedGRN.currency === 'EUR' ? '€' : '₹'}{Number(selectedGRN.rate).toLocaleString()}/MT</strong>
                        </span>
                      ) : (
                        <span className="text-xs text-rose-600 italic">no rate stored (legacy GRN)</span>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-amber-600 italic">No PO linked to this GRN</p>
                  )}
                </div>
              </div>

              {/* Material Details */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Material Details</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="col-span-2">
                      <p className="text-gray-500 text-xs">Scrap Type</p>
                      <p className="font-bold text-lg">{selectedGRN.scrapType}</p>
                    </div>
                    
                    {/* Invoice Weight Display */}
                    {selectedGRN.invoiceWeight > 0 ? (
                      <div className="col-span-2 bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-gray-500 text-xs">Invoice Weight (INV)</p>
                            <p className="font-bold text-blue-700">{selectedGRN.invoiceWeight?.toFixed(3)} {selectedGRN.unit || 'MT'}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-500 text-xs">Actual Received</p>
                            <p className="font-bold text-blue-700">{selectedGRN.netWeight?.toFixed(3)} {selectedGRN.unit || 'MT'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-gray-500 text-xs">Difference</p>
                            {(() => {
                              const diff = (selectedGRN.netWeight || 0) - (selectedGRN.invoiceWeight || 0);
                              const isShortage = diff < 0;
                              return (
                                <p className={`font-bold ${isShortage ? 'text-red-600' : 'text-green-600'}`}>
                                  {isShortage ? '' : '+'}{diff.toFixed(3)} {selectedGRN.unit || 'MT'}
                                  <span className="text-xs ml-1">({isShortage ? 'Shortage' : 'Excess'})</span>
                                </p>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="col-span-2 bg-gray-100 rounded-lg p-3 border border-dashed border-gray-300">
                        <p className="text-gray-500 text-sm text-center">
                          No Invoice Weight recorded for this GRN.
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500 text-xs">Gross Weight</p>
                      <p className="font-medium">{selectedGRN.unit === 'MT' ? selectedGRN.grossWeight?.toFixed(3) : selectedGRN.grossWeight?.toLocaleString()} {selectedGRN.unit || 'kg'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Tare Weight</p>
                      <p className="font-medium">{selectedGRN.unit === 'MT' ? selectedGRN.tareWeight?.toFixed(3) : selectedGRN.tareWeight?.toLocaleString()} {selectedGRN.unit || 'kg'}</p>
                    </div>
                    {selectedGRN.cfsWeight > 0 && (
                      <div className="col-span-2 bg-amber-50 rounded-lg p-3 border border-amber-200" data-testid="cfs-weight-detail">
                        <p className="text-amber-600 text-xs font-medium">CFS Weight</p>
                        <p className="font-bold text-amber-700 text-lg">{selectedGRN.cfsWeight?.toFixed(3)} {selectedGRN.unit || 'MT'}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500 text-xs">Net Weight (Actual Received)</p>
                      <p className="font-bold text-blue-600 text-lg">{selectedGRN.unit === 'MT' ? selectedGRN.netWeight?.toFixed(3) : selectedGRN.netWeight?.toLocaleString()} {selectedGRN.unit || 'kg'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Rate</p>
                      <p className="font-medium">{selectedGRN.currency === 'USD' ? '$' : '₹'}{selectedGRN.rate}/{selectedGRN.unit || 'kg'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-500 text-xs">Total Amount</p>
                      <p className="font-bold text-green-600 text-xl">{selectedGRN.currency === 'USD' ? '$' : '₹'}{selectedGRN.amount?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock Status */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Stock Status</h3>
                <div className="flex gap-4">
                  <div className="flex-1 bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600">Available</p>
                    <p className="font-bold text-green-700 text-xl">{selectedGRN.availableQty?.toLocaleString()} kg</p>
                  </div>
                  <div className="flex-1 bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-orange-600">Used in Production</p>
                    <p className="font-bold text-orange-700 text-xl">{selectedGRN.usedQty?.toLocaleString()} kg</p>
                  </div>
                </div>
              </div>

              {/* Remarks */}
              {selectedGRN.remarks && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-gray-500 text-xs">Remarks</p>
                  <p className="text-sm">{selectedGRN.remarks}</p>
                </div>
              )}
              
              {/* Attachments */}
              {selectedGRN.attachments && selectedGRN.attachments.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-gray-500 text-xs mb-2 flex items-center gap-1">
                    <Upload size={12} />
                    Attachments ({selectedGRN.attachments.length})
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {selectedGRN.attachments.map((file, idx) => (
                      <a 
                        key={idx}
                        href={file.data}
                        download={file.name}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition"
                      >
                        {file.type?.startsWith('image/') ? (
                          <div className="w-10 h-10 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                            <img src={file.data} alt={file.name} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <File size={18} className="text-blue-500" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-gray-700 truncate">{file.name}</p>
                          <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit GRN Modal */}
      {showEditModal && editData.grnNumber && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Edit2 className="text-amber-500" size={20} />
                <h2 className="font-bold text-lg" data-testid="edit-modal-title">Edit GRN - {editData.grnNumber}</h2>
              </div>
              <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Result & Status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Result</label>
                  <select
                    value={editData.result}
                    onChange={(e) => handleEditChange('result', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm outline-none font-medium ${
                      editData.result === 'Accepted' ? 'border-green-400 bg-green-50 text-green-700' : 'border-red-400 bg-red-50 text-red-700'
                    }`}
                    data-testid="edit-result-select"
                  >
                    <option value="Accepted">Accepted</option>
                    <option value="Shortage">Shortage</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editData.status}
                    onChange={(e) => handleEditChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none"
                    data-testid="edit-status-select"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Receiving Date & Material Type */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Receiving Date</label>
                  <input
                    type="date"
                    value={editData.receivedDate || ''}
                    onChange={(e) => handleEditChange('receivedDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="edit-received-date"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Material Type</label>
                  <input
                    type="text"
                    value={editData.scrapType || ''}
                    onChange={(e) => handleEditChange('scrapType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    placeholder="e.g. TELIC, TWITCH"
                    data-testid="edit-scrap-type"
                  />
                </div>
              </div>

              {/* Weights */}
              <div className="border-t border-gray-200 pt-3">
                <h4 className="text-xs font-semibold text-gray-600 mb-2">Weight Details (MT)</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Invoice Weight</label>
                    <input type="number" step="0.001" value={editData.invoiceWeight} onChange={(e) => handleEditChange('invoiceWeight', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none" data-testid="edit-invoice-weight" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">CFS Weight</label>
                    <input type="number" step="0.001" value={editData.cfsWeight} onChange={(e) => handleEditChange('cfsWeight', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none" data-testid="edit-cfs-weight" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Company Weight</label>
                    <input type="number" step="0.001" value={editData.companyWeight} onChange={(e) => handleEditChange('companyWeight', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none" data-testid="edit-company-weight" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Gross Weight</label>
                    <input type="number" step="0.001" value={editData.grossWeight} onChange={(e) => handleEditChange('grossWeight', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none" data-testid="edit-gross-weight" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Tare Weight</label>
                    <input type="number" step="0.001" value={editData.tareWeight} onChange={(e) => handleEditChange('tareWeight', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none" data-testid="edit-tare-weight" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Packing Weight</label>
                    <input type="number" step="0.001" value={editData.packingWeight} onChange={(e) => handleEditChange('packingWeight', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none" data-testid="edit-packing-weight" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Net Weight (auto)</label>
                    <div className="px-2 py-1.5 bg-blue-50 rounded text-sm font-bold text-blue-700">
                      {(parseFloat(editData.netWeight) || 0).toFixed(3)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Rate (from PO)</label>
                    <input type="number" value={editData.rate} readOnly
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-gray-50 text-gray-700 cursor-not-allowed" data-testid="edit-rate" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Amount (auto)</label>
                    <div className="px-2 py-1.5 bg-green-50 rounded text-sm font-bold text-green-700">
                      {(parseFloat(editData.amount) || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div className="border-t border-gray-200 pt-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                <input type="text" value={editData.remarks} onChange={(e) => handleEditChange('remarks', e.target.value)}
                  placeholder="Any notes..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none" data-testid="edit-remarks" />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowEditModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm">
                Cancel
              </button>
              <button
                onClick={handleSaveGRNEdit}
                disabled={savingGRNEdit}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm font-medium disabled:opacity-50"
                data-testid="save-edit-btn"
              >
                {savingGRNEdit ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {savingGRNEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GRN;
